import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { companiesAPI } from '../services/api';
import { Plus, Edit2, Trash2, Eye, EyeOff, Filter } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import ForceDeleteModal from '../components/ForceDeleteModal';
import CompanyModal from '../components/CompanyModal';

const Companies = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [forceDeleteModalOpen, setForceDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companies: companyPerms } = usePermissions();
  const { hasRole } = useAuth();

  // Fetch companies with status filter
  const { data: companiesData, isLoading, error } = useQuery(
    ['companies', statusFilter],
    () => companiesAPI.getAll({ is_active: statusFilter === 'all' ? undefined : statusFilter === 'active' }),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Companies query success:', data);
      },
      onError: (error) => {
        console.error('Companies fetch error:', error);
        toast.error('Failed to load companies');
      }
    }
  );

  // Delete company mutation
  const deleteMutation = useMutation(
    (uuid) => companiesAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        toast.success('Company deleted successfully!');
        setDeleteModalOpen(false);
        setCompanyToDelete(null);
      },
      onError: (error) => {
        const errorData = error.response?.data;
        // Check if it's a force delete scenario
        if (errorData?.product_count > 0 && errorData?.can_force_delete) {
          setDeleteModalOpen(false);
          setForceDeleteModalOpen(true);
          // Store additional data for force delete modal
          setCompanyToDelete({
            ...companyToDelete,
            productCount: errorData.product_count,
            canForceDelete: errorData.can_force_delete
          });
        } else {
          toast.error(errorData?.message || 'An error occurred');
        }
      },
    }
  );

  // Force delete mutation
  const forceDeleteMutation = useMutation(
    (uuid) => companiesAPI.delete(uuid, true), // true for force delete
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('companies');
        const message = response.data?.message || 'Company and products deleted successfully!';
        toast.success(message);
        setForceDeleteModalOpen(false);
        setCompanyToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to force delete company');
      },
    }
  );

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    (uuid) => companiesAPI.toggleStatus(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        toast.success('Company status updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update company status');
      },
    }
  );

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setCompanyModalOpen(true);
  };

  const handleDelete = (company) => {
    setCompanyToDelete(company);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (companyToDelete) {
      deleteMutation.mutate(companyToDelete.uuid);
    }
  };

  const handleToggleStatus = (company) => {
    toggleStatusMutation.mutate(company.uuid);
  };

  const handleForceDelete = () => {
    if (companyToDelete) {
      forceDeleteMutation.mutate(companyToDelete.uuid);
    }
  };

  const handleCreateNew = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(true);
  };

  const handleCloseModal = () => {
    setCompanyModalOpen(false);
    setSelectedCompany(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries('companies');
    handleCloseModal();
  };

  // Enhanced debug logging
  console.log('=== COMPANIES DEBUG ===');
  console.log('Raw companiesData:', companiesData);
  console.log('companiesData?.data:', companiesData?.data);
  
  // Extract companies from the correct API response structure
  let companies = [];
  
  // Axios response structure: companiesData.data = API response
  // API response structure: { success: true, data: { companies: [...], pagination: {...} } }
  // So companies are at: companiesData.data.data.companies
  if (Array.isArray(companiesData?.data?.data?.companies)) {
    companies = companiesData.data.data.companies;
    console.log('✅ Found companies in correct nested structure (data.data.companies)');
  } 
  // Fallback: Check if companies are at data.companies level
  else if (Array.isArray(companiesData?.data?.companies)) {
    companies = companiesData.data.companies;
    console.log('⚠️ Using fallback structure (data.companies)');
  }
  // Fallback: Check if companies are directly in data
  else if (Array.isArray(companiesData?.data)) {
    companies = companiesData.data;
    console.log('⚠️ Using direct data structure');
  }
  
  console.log('✅ Extracted companies count:', companies.length);
  console.log('✅ Companies data:', companies);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load companies</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Companies</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage company names and product associations
            </p>
          </div>
          {/* Add Company Button - Only show if user can create companies */}
          {companyPerms.canCreate && (
            <button
              onClick={handleCreateNew}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter by Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="all">All Companies</option>
          </select>
        </div>
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total: {companies.length} companies
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((company, index) => (
          <div
            key={company.uuid}
            className="card-modern group hover-lift animate-fade-in-up"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white shadow-glow group-hover:scale-110 transition-transform duration-300">
                  🏢
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{company.name}</h3>
              </div>
              <span
                className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                  company.is_active
                    ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                    : 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
                }`}
              >
                {company.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
              <span className="font-medium">Sort Order:</span> {company.sort_order || 0}
            </p>
            
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span>Products: {company.product_count || 0}</span>
              <span>Created: {new Date(company.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex gap-2">
              {/* Edit Button - Only show if user can update companies */}
              {companyPerms.canUpdate && (
                <button
                  onClick={() => handleEdit(company)}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </button>
              )}
              
              {/* Toggle Status Button - Only show if user can update companies */}
              {companyPerms.canUpdate && (
                <button
                  onClick={() => handleToggleStatus(company)}
                  className="text-warning-600 dark:text-warning-400 hover:text-warning-800 dark:hover:text-warning-300 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  {company.is_active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {company.is_active ? 'Deactivate' : 'Activate'}
                </button>
              )}
              
              {/* Delete Button - Only show if user can delete companies */}
              {companyPerms.canDelete && (
                <button
                  onClick={() => handleDelete(company)}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No companies message */}
      {companies.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">No companies found</div>
          {companyPerms.canCreate && (
            <button
              onClick={handleCreateNew}
              className="btn-primary inline-flex items-center px-4 py-2 rounded-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Company
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {companyModalOpen && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
        />
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Company"
        message={`Are you sure you want to delete "${companyToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />

      <ForceDeleteModal
        isOpen={forceDeleteModalOpen}
        onClose={() => setForceDeleteModalOpen(false)}
        onConfirm={handleForceDelete}
        title="Force Delete Company"
        entityName={companyToDelete?.name || ''}
        entityType="company"
        productCount={companyToDelete?.productCount || 0}
        isLoading={forceDeleteMutation.isLoading}
      />
    </div>
  );
};

export default Companies;